import { useEffect, useRef, useState } from "react";

const cache = new WeakMap();

/** Peak per bin (max |sample| across channels) — real data from the AudioBuffer, cached per buffer. */
export function peaksOf(buffer, bins) {
  const hit = cache.get(buffer);
  if (hit?.length === bins) return hit;
  const chs = Array.from({ length: buffer.numberOfChannels }, (_, c) => buffer.getChannelData(c));
  const step = Math.max(1, Math.floor(buffer.length / bins));
  const out = new Float32Array(bins);
  for (let b = 0; b < bins; b += 1) {
    let m = 0;
    const end = Math.min(buffer.length, (b + 1) * step);
    for (const d of chs) for (let i = b * step; i < end; i += 4) { const v = Math.abs(d[i]); if (v > m) m = v; }
    out[b] = m;
  }
  cache.set(buffer, out);
  return out;
}

/** Faint beat lines (every 4th brighter); only bars when beats would be too close together. */
function beatLines(g, grid, span, w, height) {
  const p = 60 / grid.bpm;
  const px = (p / span) * w;
  const every = px >= 7 ? 1 : px * 4 >= 7 ? 4 : 0;
  if (!every) return;
  for (let k = 0; grid.offset + k * p <= span; k += every) {
    g.fillStyle = k % 4 === 0 ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.05)";
    g.fillRect(((grid.offset + k * p) / span) * w, 0, 1, height);
  }
}

/** Static waveform of one or two buffers on a shared timeline, with playhead, click-to-seek and (optional) drag-to-select. */
export default function WaveCanvas({ buffer, under, duration, playhead = 0, onSeek, onSelect, selection, height = 72, color = "#fb7185", testId, grid }) {
  const ref = useRef(null);
  const down = useRef(null);
  const [drag, setDrag] = useState(null);
  const total = duration || buffer?.duration || under?.duration || 0;
  const sel = drag || selection;
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const w = cv.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr;
    cv.height = height * dpr;
    const g = cv.getContext("2d");
    g.scale(dpr, dpr);
    g.clearRect(0, 0, w, height);
    const span = total || 1;
    const draw = (buf, fill) => {
      if (!buf) return;
      const cols = Math.max(1, Math.round((buf.duration / span) * w));
      const p = peaksOf(buf, Math.min(4000, cols));
      g.fillStyle = fill;
      const bw = cols / p.length;
      p.forEach((v, i) => { const h = Math.max(1, v * (height - 4)); g.fillRect(i * bw, (height - h) / 2, Math.max(1, bw - 0.4), h); });
    };
    if (sel) {
      g.fillStyle = "rgba(252,211,77,0.16)";
      g.fillRect((sel.start / span) * w, 0, ((sel.end - sel.start) / span) * w, height);
    }
    if (grid) beatLines(g, grid, span, w, height);
    draw(under, "rgba(148,163,184,0.28)");
    draw(buffer, color);
    if (sel) {
      g.fillStyle = "#fcd34d";
      [sel.start, sel.end].forEach((t) => g.fillRect((t / span) * w - 1, 0, 2, height));
    }
    const x = Math.min(w, (playhead / span) * w);
    g.fillStyle = "#fef3c7";
    g.fillRect(x, 0, 1.5, height);
  }, [buffer, under, total, playhead, height, color, sel, grid]);
  const at = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, t: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * total };
  };
  const onDown = (e) => { if (onSeek || onSelect) { down.current = at(e); e.currentTarget.setPointerCapture?.(e.pointerId); } };
  const onMove = (e) => {
    if (!down.current || !onSelect) return;
    const p = at(e);
    if (Math.abs(p.x - down.current.x) > 5) setDrag({ start: Math.min(p.t, down.current.t), end: Math.max(p.t, down.current.t) });
  };
  const onUp = (e) => {
    const d = down.current;
    down.current = null;
    if (!d) return;
    const p = at(e);
    setDrag(null);
    if (onSelect && Math.abs(p.x - d.x) > 5 && Math.abs(p.t - d.t) >= 0.3) onSelect({ start: Math.min(p.t, d.t), end: Math.max(p.t, d.t) });
    else onSeek?.(p.t);
  };
  return (
    <canvas ref={ref} data-testid={testId} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
      className={`block w-full ${onSelect ? "touch-none cursor-crosshair" : onSeek ? "cursor-pointer" : ""}`} style={{ height }} />
  );
}
